import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { Button } from '@shieldpay/bumblebee/button';
import { Form } from '@shieldpay/bumblebee/form';
import { GrandEntrance } from '@shieldpay/bumblebee/grand-entrance';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Infoblock } from '@shieldpay/bumblebee/infoblock';
import { Layout } from '@shieldpay/bumblebee/layout';
import { PasswordInput } from '@shieldpay/bumblebee/password-input';
import { Text } from '@shieldpay/bumblebee/text';
import { ValidationChecklist } from '@shieldpay/bumblebee/validation-checklist';

import {
  containsLowerCase,
  containsNumber,
  containsSpecialCharacter,
  containsUpperCase,
  createMaxLength,
  createMinLength,
} from '@shieldpay/utility-functions-ui/validators/string';

export type NewPasswordFormProps = {
  submitting?: boolean;
  onSubmit: (data: NewPasswordFields) => void;
};

const FieldSchema = z.object({
  newPassword: z
    .string()
    .min(8)
    .max(256)
    .refine((val) => {
      return (
        containsLowerCase(val) &&
        containsUpperCase(val) &&
        containsNumber(val) &&
        containsSpecialCharacter(val)
      );
    }),
});

export type NewPasswordFields = z.infer<typeof FieldSchema>;

export const NewPasswordForm = ({
  onSubmit,
  submitting,
}: NewPasswordFormProps) => {
  const { formatMessage } = useIntl();
  const [showChecklist, setShowChecklist] = useState(false);

  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      return parseWithZod(formData, { schema: FieldSchema });
    },

    onSubmit: (e, { submission }) => {
      // Prevent the default form submission - auth is handled client-side
      e.preventDefault();

      onSubmit(FieldSchema.parse(submission?.payload));
    },
  });

  const value = fields.newPassword.value;

  const validationState = value
    ? {
        minLength: createMinLength(8)(value),
        maxLength: createMaxLength(256)(value),
        containsSpecialCharacter: containsSpecialCharacter(value),
        containsLowerCase: containsLowerCase(value),
        containsNumber: containsNumber(value),
        containsUpperCase: containsUpperCase(value),
      }
    : null;

  return (
    <Form
      {...getFormProps(form)}
      spacing="basePos3"
      title={formatMessage({
        id: 'login.details',
      })}
    >
      <Heading>
        <FormattedMessage id="login.createPassword" />
      </Heading>

      <Layout spacing="baseNeg4">
        <PasswordInput
          label={<FormattedMessage id="shared.password" />}
          {...getInputProps(fields.newPassword, { type: 'password' })}
          onFocus={() => {
            setShowChecklist(true);
          }}
          hidePasswordText={<FormattedMessage id="login.hidePassword" />}
          showPasswordText={<FormattedMessage id="login.showPassword" />}
        />
        <GrandEntrance show={showChecklist}>
          <Layout spacing="baseNeg4">
            <Text paragraph variant="caption150">
              <FormattedMessage id="login.validation.password.atLeast" />
            </Text>
            <ValidationChecklist
              displayState={
                !form.valid && !!form.value ? 'error' : 'incomplete'
              }
              items={[
                {
                  text: formatMessage({
                    id: 'login.validation.password.minLength',
                  }),
                  valid: !!validationState?.minLength,
                },
                {
                  text: formatMessage({
                    id: 'login.validation.password.containsUpperCase',
                  }),
                  valid: !!validationState?.containsUpperCase,
                },
                {
                  text: formatMessage({
                    id: 'login.validation.password.containsLowerCase',
                  }),
                  valid: !!validationState?.containsLowerCase,
                },
                {
                  text: formatMessage({
                    id: 'login.validation.password.containsNumber',
                  }),
                  valid: !!validationState?.containsNumber,
                },
                {
                  text: formatMessage({
                    id: 'login.validation.password.containsSpecialCharacter',
                  }),
                  valid: !!validationState?.containsSpecialCharacter,
                },
              ]}
            />
            
            {!form.valid && validationState?.maxLength === false ? (
              <Infoblock variant="error">
                <FormattedMessage id="login.validation.password.maxLength" />
              </Infoblock>
            ) : null}
          </Layout>
        </GrandEntrance>
      </Layout>
      <Button size="large" type="submit" loading={submitting}>
        <FormattedMessage id="shared.continue" />
      </Button>
    </Form>
  );
};
